import { useEffect, useRef, useState } from 'react';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

/**
 * useRecaptcha — reusable hook for Google reCAPTCHA v2 (checkbox) widget.
 *
 * Usage:
 *   const { containerRef, token, ready, reset } = useRecaptcha();
 *   ...
 *   {RECAPTCHA_SITE_KEY && <div ref={containerRef} />}
 *
 * The hook lazy-loads Google's api.js, renders an explicit widget into the
 * container ref, and exposes the current token + a reset() to call after
 * any submission failure.
 */
export function useRecaptcha() {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [token, setToken] = useState('');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) return;
    const render = () => {
      if (!window.grecaptcha?.render || !containerRef.current || widgetIdRef.current !== null) return;
      try {
        widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
          sitekey: RECAPTCHA_SITE_KEY,
          theme: 'light',
          callback: (t) => setToken(t || ''),
          'expired-callback': () => setToken(''),
          'error-callback': () => setToken(''),
        });
        setReady(true);
      } catch { /* widget already rendered */ }
    };
    if (window.grecaptcha?.render) {
      render();
      return;
    }
    if (!document.querySelector('script[data-recaptcha-loader="1"]')) {
      const s = document.createElement('script');
      s.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
      s.async = true; s.defer = true;
      s.setAttribute('data-recaptcha-loader', '1');
      document.body.appendChild(s);
    }
    const t = setInterval(() => {
      if (window.grecaptcha?.render) { clearInterval(t); render(); }
    }, 200);
    return () => clearInterval(t);
  }, []);

  const reset = () => {
    if (widgetIdRef.current !== null && window.grecaptcha) {
      try { window.grecaptcha.reset(widgetIdRef.current); } catch { /* ignore */ }
    }
    setToken('');
  };

  return { containerRef, token, ready, reset, enabled: !!RECAPTCHA_SITE_KEY };
}
